import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { http } from '../api/client'
import { Endpoints } from '../api/endpoints'
import { Search, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

interface SearchResult {
  id: string
  type: string
  title: string
  description: string | null
  url: string
  relevance: number
}

const typeIcons: Record<string, string> = {
  Todo: '✓',
  Habit: '🔄',
  Goal: '🎯',
  Event: '📅',
  Journal: '📝',
  Note: '📚',
  Reading: '📖',
  Card: '📋',
}

const typeColors: Record<string, string> = {
  Todo: 'text-blue-600 dark:text-blue-400',
  Habit: 'text-green-600 dark:text-green-400',
  Goal: 'text-purple-600 dark:text-purple-400',
  Event: 'text-orange-600 dark:text-orange-400',
  Journal: 'text-pink-600 dark:text-pink-400',
  Note: 'text-indigo-600 dark:text-indigo-400',
  Reading: 'text-yellow-600 dark:text-yellow-400',
  Card: 'text-teal-600 dark:text-teal-400',
}

export function GlobalSearch() {
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const navigate = useNavigate()

  // Keyboard shortcut: Cmd+K / Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setIsOpen(prev => !prev)
      }

      if (isOpen && e.key === 'Escape') {
        setIsOpen(false)
        setQuery('')
        setResults([])
      }

      if (isOpen && results.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault()
          setSelectedIndex(prev => (prev + 1) % results.length)
        } else if (e.key === 'ArrowUp') {
          e.preventDefault()
          setSelectedIndex(prev => (prev - 1 + results.length) % results.length)
        } else if (e.key === 'Enter') {
          e.preventDefault()
          if (results[selectedIndex]) {
            handleSelect(results[selectedIndex])
          }
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, results, selectedIndex])

  // Search on query change
  useEffect(() => {
    if (!query.trim()) {
      setResults([])
      return
    }

    const debounce = setTimeout(async () => {
      setLoading(true)
      try {
        const data = await http.get<SearchResult[]>(Endpoints.search(query))
        setResults(data)
        setSelectedIndex(0)
      } catch {
        // Error handled by interceptor
      } finally {
        setLoading(false)
      }
    }, 300)

    return () => clearTimeout(debounce)
  }, [query])

  const handleSelect = (result: SearchResult) => {
    navigate(result.url)
    setIsOpen(false)
    setQuery('')
    setResults([])
  }

  if (!isOpen) return null

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] px-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 dark:bg-black/70"
        onClick={() => setIsOpen(false)}
      />

      {/* Search Modal */}
      <div className="relative w-full max-w-2xl bg-background rounded-lg shadow-2xl border">
        {/* Search Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b">
          <Search className="w-5 h-5 text-muted-foreground" />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search todos, habits, goals, events..."
            className="flex-1 bg-transparent outline-none text-base"
            autoFocus
          />
          <button
            onClick={() => setIsOpen(false)}
            className="p-1 hover:bg-muted rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto">
          {loading && (
            <div className="p-8 text-center text-muted-foreground">
              Searching...
            </div>
          )}

          {!loading && query && results.length === 0 && (
            <div className="p-8 text-center text-muted-foreground">
              No results found for "{query}"
            </div>
          )}

          {!loading && results.length > 0 && (
            <div className="py-2">
              {results.map((result, index) => (
                <button
                  key={result.id}
                  onClick={() => handleSelect(result)}
                  className={`w-full px-4 py-3 flex items-start gap-3 hover:bg-muted transition-colors text-left ${
                    index === selectedIndex ? 'bg-muted' : ''
                  }`}
                >
                  <span className="text-2xl mt-0.5">
                    {typeIcons[result.type] || '📄'}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium truncate">{result.title}</h3>
                      <span className={`text-xs font-medium ${typeColors[result.type] || 'text-gray-600'}`}>
                        {result.type}
                      </span>
                    </div>
                    {result.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">
                        {result.description}
                      </p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-2 border-t text-xs text-muted-foreground">
          <span>
            <kbd className="px-2 py-1 bg-muted rounded text-xs">↑↓</kbd> Navigate
            <kbd className="ml-2 px-2 py-1 bg-muted rounded text-xs">↵</kbd> Select
          </span>
          <span>
            <kbd className="px-2 py-1 bg-muted rounded text-xs">ESC</kbd> Close
          </span>
        </div>
      </div>
    </div>,
    document.body
  )
}
